import { type QueryClient, useQuery } from "@tanstack/react-query";

import {
  countUploadedSourcesByTenant,
  type UploadHistoryItem,
} from "@/services/uploads";

// The query key is a child of ["tenantUploadHistory"], so broad history
// invalidations still reach this query. Flows that are known to change review
// counts can additionally call refetchPendingReviewUploadsBurst to catch the
// moment the backend reflects the new status without lowering the global poll.
const POLL_INTERVAL_MS = 60_000;
const STALE_MS = 15_000;
export const PENDING_REVIEW_BADGE_CAP = 99;

export const PENDING_REVIEW_UPLOADS_QUERY_KEY = [
  "tenantUploadHistory",
  "pending-review",
];
export const PENDING_REVIEW_OPTIMISTIC_QUERY_KEY = [
  "pendingReviewUploadsOptimistic",
];
const BURST_REFETCH_DELAYS_MS = [0, 5_000, 10_000];
const OPTIMISTIC_ADJUSTMENT_TTL_MS = 15_000;
const RECONCILE_POLL_INTERVAL_MS = 5_000;

interface PendingReviewUploadsSummary {
  count: number;
}

type PendingReviewAdjustmentDirection = "increment" | "decrement";

interface PendingReviewOptimisticAdjustment {
  id: string;
  direction: PendingReviewAdjustmentDirection;
  targetCount: number;
  createdAt: number;
}

interface PendingReviewOptimisticState {
  adjustments: PendingReviewOptimisticAdjustment[];
}

const EMPTY_OPTIMISTIC_STATE: PendingReviewOptimisticState = {
  adjustments: [],
};

export function countPendingReviewUploads(
  uploads: UploadHistoryItem[] | undefined,
): number {
  if (!uploads) return 0;
  return uploads.filter((upload) => upload.status === "review").length;
}

async function fetchPendingReviewUploadsSummary() {
  const response = await countUploadedSourcesByTenant("review");

  return { count: response.data.count };
}

export function refetchPendingReviewUploads(queryClient: QueryClient) {
  void queryClient
    .refetchQueries({
      queryKey: PENDING_REVIEW_UPLOADS_QUERY_KEY,
      type: "active",
    })
    .finally(() => {
      prunePendingReviewOptimisticAdjustments(queryClient);
    });
}

export function pollPendingReviewUploadsUntilSettled(queryClient: QueryClient) {
  const startedAt = Date.now();

  const poll = () => {
    void queryClient
      .refetchQueries({
        queryKey: PENDING_REVIEW_UPLOADS_QUERY_KEY,
        type: "active",
      })
      .finally(() => {
        prunePendingReviewOptimisticAdjustments(queryClient);
        const serverCount = getPendingReviewServerCount(queryClient);
        const optimisticState =
          queryClient.getQueryData<PendingReviewOptimisticState>(
            PENDING_REVIEW_OPTIMISTIC_QUERY_KEY,
          ) ?? EMPTY_OPTIMISTIC_STATE;
        const hasActiveAdjustment = optimisticState.adjustments.some(
          (adjustment) =>
            isPendingReviewAdjustmentActive(adjustment, serverCount),
        );

        if (
          hasActiveAdjustment &&
          Date.now() - startedAt < OPTIMISTIC_ADJUSTMENT_TTL_MS
        ) {
          window.setTimeout(poll, RECONCILE_POLL_INTERVAL_MS);
        }
      });
  };

  poll();
}

export function refetchPendingReviewUploadsBurst(queryClient: QueryClient) {
  for (const delay of BURST_REFETCH_DELAYS_MS) {
    window.setTimeout(() => {
      refetchPendingReviewUploads(queryClient);
    }, delay);
  }
}

function getPendingReviewServerCount(queryClient: QueryClient) {
  return (
    queryClient.getQueryData<PendingReviewUploadsSummary>(
      PENDING_REVIEW_UPLOADS_QUERY_KEY,
    )?.count ?? 0
  );
}

function isPendingReviewAdjustmentActive(
  adjustment: PendingReviewOptimisticAdjustment,
  serverCount: number,
  now = Date.now(),
) {
  if (now - adjustment.createdAt > OPTIMISTIC_ADJUSTMENT_TTL_MS) {
    return false;
  }

  if (adjustment.direction === "increment") {
    return serverCount < adjustment.targetCount;
  }

  return serverCount > adjustment.targetCount;
}

function getPendingReviewOptimisticDelta(
  adjustments: PendingReviewOptimisticAdjustment[],
  serverCount: number,
) {
  return adjustments.reduce((total, adjustment) => {
    if (!isPendingReviewAdjustmentActive(adjustment, serverCount)) {
      return total;
    }

    return total + (adjustment.direction === "increment" ? 1 : -1);
  }, 0);
}

function prunePendingReviewOptimisticAdjustments(queryClient: QueryClient) {
  const serverCount = getPendingReviewServerCount(queryClient);

  queryClient.setQueryData<PendingReviewOptimisticState>(
    PENDING_REVIEW_OPTIMISTIC_QUERY_KEY,
    (current = EMPTY_OPTIMISTIC_STATE) => ({
      adjustments: current.adjustments.filter((adjustment) =>
        isPendingReviewAdjustmentActive(adjustment, serverCount),
      ),
    }),
  );
}

function createPendingReviewAdjustmentId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random()}`;
}

export function addPendingReviewCountAdjustment(
  queryClient: QueryClient,
  direction: PendingReviewAdjustmentDirection,
) {
  const serverCount = getPendingReviewServerCount(queryClient);
  const currentOptimisticState =
    queryClient.getQueryData<PendingReviewOptimisticState>(
      PENDING_REVIEW_OPTIMISTIC_QUERY_KEY,
    ) ?? EMPTY_OPTIMISTIC_STATE;
  const currentDelta = getPendingReviewOptimisticDelta(
    currentOptimisticState.adjustments,
    serverCount,
  );
  const currentCount = Math.max(0, serverCount + currentDelta);
  const targetCount =
    direction === "increment"
      ? currentCount + 1
      : Math.max(0, currentCount - 1);

  queryClient.setQueryData<PendingReviewOptimisticState>(
    PENDING_REVIEW_OPTIMISTIC_QUERY_KEY,
    (current = EMPTY_OPTIMISTIC_STATE) => ({
      adjustments: [
        ...current.adjustments,
        {
          id: createPendingReviewAdjustmentId(),
          direction,
          targetCount,
          createdAt: Date.now(),
        },
      ],
    }),
  );
}

interface UsePendingReviewUploadsOptions {
  enabled?: boolean;
}

export function usePendingReviewUploads({
  enabled = true,
}: UsePendingReviewUploadsOptions = {}) {
  const query = useQuery({
    queryKey: PENDING_REVIEW_UPLOADS_QUERY_KEY,
    queryFn: fetchPendingReviewUploadsSummary,
    enabled,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    staleTime: STALE_MS,
    retry: 1,
  });
  const optimisticQuery = useQuery({
    queryKey: PENDING_REVIEW_OPTIMISTIC_QUERY_KEY,
    queryFn: () => EMPTY_OPTIMISTIC_STATE,
    initialData: EMPTY_OPTIMISTIC_STATE,
    staleTime: Infinity,
  });
  const serverCount = query.data?.count ?? 0;
  const count = Math.max(
    0,
    serverCount +
      getPendingReviewOptimisticDelta(
        optimisticQuery.data.adjustments,
        serverCount,
      ),
  );

  return {
    count,
    hasPendingReview: count > 0,
    isLoading: query.isLoading,
    isRefreshing: query.isFetching && !query.isLoading,
  };
}
